import clsx from "clsx";
import { History } from "lucide-react";
import type {
  ActivityEventView,
  JsonValue,
} from "../../../../shared/contracts.js";
import { Button } from "../../../ui/Button.js";
import {
  describeActivityEvent,
  formatInstant,
  type ActivityContext,
} from "./activity-sentences.js";

// The instant and event-type words live with the dictionary; re-exported for
// the pages that take them from the timeline.
export { formatInstant, humanizeEventType } from "./activity-sentences.js";

/**
 * The immutable activity history of a case (spec §13, §7 of the 2026-09-17
 * spec): every event as one plain sentence, with its actor and timestamp on
 * a muted second line and the previous → new value of each changed field
 * behind a closed Details disclosure, newest first, exactly as the server
 * recorded it.
 *
 * The timeline never edits, merges or drops an event. When the server has
 * older events than the page it returned, the caller offers them through
 * `onShowOlder` ("collapse older activity, never discard it").
 */

export interface ActivityTimelineProps {
  events: readonly ActivityEventView[];
  /**
   * What the sentences may name: the detail's lines and the people it knows
   * (see `ActivityContext`). Defaults to the service side with nothing named.
   */
  context?: ActivityContext;
  /** The server reported more events than are listed. */
  hasOlder?: boolean;
  onShowOlder?: () => void;
  isLoadingOlder?: boolean;
  /** A failed request for older events, in plain language (no live region: the parent owns those). */
  olderError?: string | null;
  className?: string;
}

const SERVICE_CONTEXT: ActivityContext = { side: "service" };

export function jsonLabel(value: JsonValue): string {
  if (value === null) return "—";
  if (typeof value === "string") return value === "" ? "—" : value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  return JSON.stringify(value);
}

export interface ActivityEventEntryProps {
  event: ActivityEventView;
  context: ActivityContext;
  /**
   * What the entry's class names start with — `inspector__event-` in the
   * Parts inspector, `activity__` in the shared detail — so each surface
   * keeps its own stylesheet while the markup and the words stay one.
   */
  classPrefix: string;
}

/**
 * The body of one timeline entry: the sentence, then "by <actor> · <time>",
 * then — only when the event changed something — a closed `<details>` with
 * one labelled "previous → next" row per change. The visible text is the
 * whole accessible text; nothing is hidden from or added for a screen reader.
 */
export function ActivityEventEntry({ event, context, classPrefix }: ActivityEventEntryProps) {
  const { sentence, details } = describeActivityEvent(event, context);
  return (
    <>
      <p className={`${classPrefix}sentence`}>{sentence}</p>
      <p className={`${classPrefix}meta`}>
        by <span className={`${classPrefix}actor`}>{event.actorName ?? "System"}</span>
        {" · "}
        <time dateTime={event.createdAt}>{formatInstant(event.createdAt)}</time>
      </p>
      {details.length > 0 ? (
        <details className={`${classPrefix}details`}>
          <summary>Details</summary>
          <ul className={`${classPrefix}changes`}>
            {details.map((detail, index) => (
              <li key={`${event.id}-${index}`} className={`${classPrefix}change`}>
                <span className={`${classPrefix}change-field`}>{detail.label}:</span>{" "}
                <span className={`${classPrefix}change-values`}>
                  {detail.previous} → {detail.next}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </>
  );
}

export function ActivityTimeline({
  events,
  context = SERVICE_CONTEXT,
  hasOlder = false,
  onShowOlder,
  isLoadingOlder = false,
  olderError = null,
  className,
}: ActivityTimelineProps) {
  if (events.length === 0) {
    return (
      <p className={clsx("inspector__empty", className)}>
        No activity recorded yet.
      </p>
    );
  }

  return (
    <div className={clsx("activity", className)}>
      <ol className="inspector__activity">
        {events.map((event) => (
          <li key={event.id} className="inspector__event">
            <ActivityEventEntry event={event} context={context} classPrefix="inspector__event-" />
          </li>
        ))}
      </ol>
      {hasOlder && onShowOlder ? (
        <div className="activity__older">
          <Button
            variant="quiet"
            icon={<History />}
            busy={isLoadingOlder}
            onClick={onShowOlder}
          >
            Show older activity
          </Button>
          {olderError ? (
            <p className="activity__older-error">{olderError}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
